import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CURRENCIES, currencyForCountry, formatCurrency, formatCurrencyCompact } from "../data/currencies";

const CurrencyContext = createContext(null);
const STORAGE_KEY = "mb_currency_code";
const DETECTED_SESSION_KEY = "mb_currency_detected";
const GEO_ENDPOINT = "https://get.geojs.io/v1/ip/country.json";

// Instant, offline guess from the browser's locale (e.g. "en-IN" -> "IN"),
// used while the IP-based lookup below is in flight.
function localeGuessCurrency(detectCurrency) {
  const locale = typeof navigator !== "undefined" ? navigator.language : "";
  const region = locale?.split("-")[1];
  return detectCurrency(region);
}

// ?currency=AED / ?currency=USD on any page lets a shared link fix the
// default currency, taking priority over geo-detection and the saved
// preference for this load (but isn't itself saved to sessionStorage).
function currencyFromUrl() {
  if (typeof window === "undefined") return null;
  const code = new URLSearchParams(window.location.search).get("currency")?.toUpperCase();
  return code ? CURRENCIES.find((c) => c.code === code) || null : null;
}

// `detectCurrency` maps a country code to the default currency - pages can
// override the site-wide mapping (e.g. INR for India, USD everywhere else).
export function CurrencyProvider({ children, detectCurrency = currencyForCountry }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCurrency = currencyFromUrl();
  const [currency, setCurrencyState] = useState(() => {
    if (urlCurrency) return urlCurrency;
    const saved = typeof window !== "undefined" ? sessionStorage.getItem(STORAGE_KEY) : null;
    if (saved) return CURRENCIES.find((c) => c.code === saved) || CURRENCIES[0];
    return localeGuessCurrency(detectCurrency);
  });
  const manualOverride = useRef(
    Boolean(urlCurrency || (typeof window !== "undefined" && sessionStorage.getItem(STORAGE_KEY)))
  );

  useEffect(() => {
    if (manualOverride.current) return;

    const cached = sessionStorage.getItem(DETECTED_SESSION_KEY);
    if (cached) {
      setCurrencyState(detectCurrency(cached));
      return;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);

    fetch(GEO_ENDPOINT, { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        if (!data?.country) return;
        sessionStorage.setItem(DETECTED_SESSION_KEY, data.country);
        if (manualOverride.current) return;
        setCurrencyState(detectCurrency(data.country));
      })
      .catch(() => {
        // Silently keep the locale-based guess if geolocation fails/blocked.
      })
      .finally(() => clearTimeout(timeout));

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [detectCurrency]);

  const setCurrency = (next) => {
    manualOverride.current = true;
    sessionStorage.setItem(STORAGE_KEY, next.code);
    setCurrencyState(next);
    const params = new URLSearchParams(searchParams);
    params.set("currency", next.code);
    setSearchParams(params, { replace: true });
  };

  const value = {
    currency,
    setCurrency,
    currencies: CURRENCIES,
    format: (inrAmount) => formatCurrency(inrAmount, currency),
    formatCompact: (inrAmount) => formatCurrencyCompact(inrAmount, currency),
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within a CurrencyProvider");
  return ctx;
}
